/**
 * #1959（O31-7）: admin-ops「機能利用」画面。
 *
 * 運営が、機能ごとの使われ方を workspace 単位で読み、日次所見の根拠を確かめ、改修の前後を比べる面。
 * **見る面であって、起票する・直す・対応表を編集する面ではない**（書き込みの操作は無い）。
 * 正本: `spec/sales-counter/screen-contracts/admin-ops-feature-usage.md`（塊 1〜4）/ State Matrix 同名 /
 * Experience Spec の W / U 群（文言）。型は O30「AI 利用」（`pages/AiUsage/`）に揃える。
 *
 * - 塊 1 所見（`InsightPanel`）と塊 3 内訳（`UsageTables`）は別々に取得する（L5）。
 * - 期間・タブ・絞り込み・基準日・長さ・所見の日付は URL に持つ。不正な値は既定に戻す（D9）。
 * - 守るライン #1: 要素の表示文字・入力値・利用者 id・個別のセッションを出さない。利用者は人数だけ。
 *
 * PC 専用（min-width: 1024px、他の admin-ops 画面と同じ）。
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  type FeatureUsageConfigured,
  FeatureUsageFetchError,
  type FeatureUsagePeriod,
  fetchFeatureUsage,
  type UsageTab,
} from '../../lib/feature-usage-api';
import { dayWithWeekday, jstTime, shiftDay, workspaceName } from './format';
import { InsightPanel } from './InsightPanel';
import { type SortKey, type SortState, UsageTables } from './UsageTables';

export interface FeatureUsagePageProps {
  apiBaseUrl: string;
}

const PERIODS: Array<[FeatureUsagePeriod, string]> = [
  ['today', '今日'],
  ['yesterday', '昨日'],
  ['7d', '7 日'],
  ['28d', '28 日'],
  ['compare', '比較'],
];
const TABS: Array<[UsageTab, string]> = [
  ['features', '機能別'],
  ['screens', '画面別'],
  ['transitions', '遷移の流れ'],
  ['abandoned', '途中でやめた'],
  ['unused', '使われていない'],
];
const COMPARE_LENGTHS = [7, 14, 28];
const DEFAULT_PERIOD: FeatureUsagePeriod = '7d';

const isDay = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);
const isTab = (v: string | null): v is UsageTab => TABS.some(([t]) => t === v);
const todayJst = () => new Date(Date.now() + 9 * 3_600_000).toISOString().slice(0, 10);

/** URL から API に渡すクエリを作る。不正な組み合わせは既定（7 日）に戻す（D9）。 */
function apiQuery(params: URLSearchParams): {
  period: FeatureUsagePeriod;
  query: Record<string, string>;
} {
  const p = params.get('period');
  const ws = params.get('workspace');
  const withWs = (q: Record<string, string>) => (ws ? { ...q, workspace: ws } : q);
  if (p === 'today' || p === 'yesterday' || p === '7d' || p === '28d') {
    return { period: p, query: withWs({ period: p }) };
  }
  if (p === 'range' && isDay(params.get('from')) && isDay(params.get('to'))) {
    return {
      period: 'range',
      query: withWs({
        period: 'range',
        from: params.get('from') as string,
        to: params.get('to') as string,
      }),
    };
  }
  if (p === 'compare') {
    const custom = ['aFrom', 'aTo', 'bFrom', 'bTo'].map((k) => params.get(k));
    if (custom.every(isDay)) {
      const [aFrom = '', aTo = '', bFrom = '', bTo = ''] = custom as string[];
      return { period: 'compare', query: withWs({ period: 'compare', aFrom, aTo, bFrom, bTo }) };
    }
    const days = Number(params.get('days') ?? 7);
    const len = COMPARE_LENGTHS.includes(days) ? days : 7;
    const base = params.get('base');
    const b = isDay(base) ? base : shiftDay(todayJst(), -len);
    return { period: 'compare', query: withWs({ period: 'compare', base: b, days: String(len) }) };
  }
  return { period: DEFAULT_PERIOD, query: withWs({ period: DEFAULT_PERIOD }) };
}

/** 塊 2 — 比較の指定（U-11 / U-12 / U-13）。 */
function CompareControls({
  data,
  params,
  update,
}: {
  data: FeatureUsageConfigured | null;
  params: URLSearchParams;
  update: (patch: Record<string, string | null>) => void;
}) {
  const [custom, setCustom] = useState(params.has('aFrom'));
  const days = Number(params.get('days') ?? 7);
  const cmp = data?.compare ?? null;
  return (
    <div className="mt-3 space-y-2 text-sm text-slate-700">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2">
          基準日
          <input
            type="date"
            value={cmp?.base ?? params.get('base') ?? ''}
            onChange={(e) =>
              update({ base: e.target.value, aFrom: null, aTo: null, bFrom: null, bTo: null })
            }
            className="rounded border border-slate-300 px-2 py-0.5"
          />
        </label>
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
          {COMPARE_LENGTHS.map((n) => (
            <button
              key={n}
              type="button"
              aria-pressed={!custom && (COMPARE_LENGTHS.includes(days) ? days : 7) === n}
              onClick={() => {
                setCustom(false);
                update({ days: String(n), aFrom: null, aTo: null, bFrom: null, bTo: null });
              }}
              className="rounded px-2 py-0.5 text-xs text-slate-600 aria-pressed:bg-slate-900 aria-pressed:text-white"
            >
              {n} 日
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setCustom((v) => !v)}
          className="text-xs text-slate-600 underline"
        >
          期間を個別に指定
        </button>
      </div>
      {custom && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {(['aFrom', 'aTo', 'bFrom', 'bTo'] as const).map((k) => (
            <label key={k} className="flex items-center gap-1">
              {{ aFrom: 'A 開始', aTo: 'A 終了', bFrom: 'B 開始', bTo: 'B 終了' }[k]}
              <input
                type="date"
                value={params.get(k) ?? ''}
                onChange={(e) => update({ [k]: e.target.value })}
                className="rounded border border-slate-300 px-1 py-0.5"
              />
            </label>
          ))}
        </div>
      )}
      {cmp && (
        <>
          <p className="tabular-nums">
            期間 A {cmp.a.fromDay}〜{cmp.a.toDay} / 期間 B {cmp.b.fromDay}〜{cmp.b.toDay}
          </p>
          {data?.previousTables && (
            <p className="text-xs text-slate-500">
              A: 記録のあった日 {data.previousTables.meta.recordDays} 日 ・ 顧客{' '}
              {data.previousTables.meta.customerWorkspaces} workspace
            </p>
          )}
          {data && (
            <p className="text-xs text-slate-500">
              B: 記録のあった日 {data.tables.meta.recordDays} 日 ・ 顧客{' '}
              {data.tables.meta.customerWorkspaces} workspace
            </p>
          )}
          {!cmp.sameLength && (
            <p className="text-xs text-slate-600">
              期間の長さが違うため、1 日あたりの件数で並べています。
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** 塊 4 — 注記（U-17 / U-18 / U-22 / U-23 / U-33）。固定文は読み込み前から出す。 */
function Notes({
  data,
  mappingOpen,
  onToggleMapping,
}: {
  data: FeatureUsageConfigured | null;
  mappingOpen: boolean;
  onToggleMapping: () => void;
}) {
  const t = data?.tables;
  const alysLine = data
    ? data.alysWorkspaces.length === 0
      ? '自社として分けている workspace: なし（すべて顧客として数えています）'
      : `自社として分けている workspace: ${data.alysWorkspaces
          .map((w) => workspaceName({ workspaceId: w.workspaceId, name: w.name ?? '' }))
          .join('、')}`
    : null;
  const showCollectionNote =
    !!t && (t.collection.customer === 'not_started' || t.collection.sinceDay);
  return (
    <section className="mt-8 space-y-1 text-xs text-slate-600">
      {alysLine && <p>{alysLine}</p>}
      {showCollectionNote && (
        <p className="text-amber-700">顧客 workspace の記録は、収集を始めた日から集まります。</p>
      )}
      {data?.captureStartedAt && <p>顧客の収集: {data.captureStartedAt} から</p>}
      {data && data.range.fromDay < data.rawRetentionFrom && (
        <p>
          90 日より前の分は日ごとの集計だけが残っています。滞在時間 は {data.rawRetentionFrom}{' '}
          以降の分です。
        </p>
      )}
      {data && <p>対応表: {data.mappingUpdatedAt} 時点</p>}
      <p>
        所見は前日と直近 7 日を、その前の 7
        日と比べた集計値だけを使って自動で作成しています（個々の操作・画面の内容は使っていません）。
      </p>
      <p>
        記録するのは画面・要素の識別子・時刻・セッション・workspace・ユーザーの区別・エラーコードです（画面には人数だけを出します）。表示文字・入力値・リードの情報は記録していません。
      </p>
      <p>生の記録は 90 日で消え、日ごとの集計は残ります。</p>
      {data && (
        <button type="button" onClick={onToggleMapping} className="mt-2 text-slate-700 underline">
          対応表を見る
        </button>
      )}
      {data && mappingOpen && (
        <div data-testid="feature-usage-mapping" className="mt-2 space-y-3">
          <table className="w-auto">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="pr-6 font-medium">機能</th>
                <th className="pr-6 font-medium">画面</th>
                <th className="font-medium">要素の識別子</th>
              </tr>
            </thead>
            <tbody>
              {data.mapping.features.map((f) => (
                <tr key={f.feature} className="align-top">
                  <td className="pr-6">{f.feature}</td>
                  <td className="pr-6 font-mono">{f.screens.join(', ') || '—'}</td>
                  <td className="font-mono">{f.elements.join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="w-auto">
            <thead>
              <tr className="text-left text-slate-500">
                <th className="pr-6 font-medium">流れ</th>
                <th className="pr-6 font-medium">始まり</th>
                <th className="font-medium">完了</th>
              </tr>
            </thead>
            <tbody>
              {data.mapping.flows.map((f) => (
                <tr key={f.key} className="align-top">
                  <td className="pr-6">{f.label}</td>
                  <td className="pr-6 font-mono">{f.startElements.join(', ')}</td>
                  <td>
                    {f.completeAuditActions.map((a) => (
                      <span key={a} className="block">
                        {a}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function FeatureUsagePage({ apiBaseUrl }: FeatureUsagePageProps) {
  const [params, setParams] = useSearchParams();
  const [mappingOpen, setMappingOpen] = useState(false);
  const { period, query: q } = apiQuery(params);
  const tab: UsageTab = isTab(params.get('tab')) ? (params.get('tab') as UsageTab) : 'features';
  const sortParam = params.get('sort');
  const sort: SortState = {
    key:
      sortParam === 'clicks' || sortParam === 'views' || sortParam === 'lastDay' ? sortParam : null,
    dir: params.get('dir') === 'asc' ? 'asc' : 'desc',
  };
  /** 同じ見出しは降順 ⇄ 昇順、別の見出しはその列の降順（D11）。 */
  const toggleSort = (k: SortKey) =>
    update({ sort: k, dir: sort.key === k && sort.dir === 'desc' ? 'asc' : null });
  const insightDay = isDay(params.get('insightDay')) ? params.get('insightDay') : null;

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setParams(next);
  };

  const query = useQuery({
    queryKey: ['admin-ops-feature-usage', q],
    queryFn: () => fetchFeatureUsage(apiBaseUrl, q),
    // 切替中は直前の表を薄く残す（一瞬 0 や空に見せない・L2）。失敗したら残さない（E2）。
    placeholderData: keepPreviousData,
    retry: false,
  });
  const data = query.data;
  const configured = data?.configured ? data : null;
  const switching = query.isPlaceholderData && query.isFetching;
  const errorKind =
    query.error instanceof FeatureUsageFetchError
      ? query.error.kind
      : query.error
        ? 'failed'
        : null;
  const filter = configured?.tables.meta.workspace ?? null;

  const selectPeriod = (p: FeatureUsagePeriod) =>
    update({
      period: p,
      from: null,
      to: null,
      base: null,
      days: null,
      aFrom: null,
      aTo: null,
      bFrom: null,
      bTo: null,
    });

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="hidden lg:block">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <header className="mb-6">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              ALYS Internal / admin-ops
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">機能利用</h1>
            <p className="mt-2 text-sm text-slate-600">
              管理画面の画面・要素・機能ごとの使われ方です。workspace
              が少ないうちは、傾向ではなく個別の観察として読んでください。
            </p>
          </header>

          {/* 塊 1 — 所見（期間の選択には連動しない） */}
          {!(data && !data.configured) && (
            <InsightPanel
              apiBaseUrl={apiBaseUrl}
              day={insightDay}
              onDayChange={(d) => update({ insightDay: d })}
              onOpenTable={(link) =>
                update({
                  period: 'range',
                  from: link.fromDay,
                  to: link.toDay,
                  tab: link.tab,
                  workspace: link.workspaceId,
                  base: null,
                  days: null,
                  aFrom: null,
                  aTo: null,
                  bFrom: null,
                  bTo: null,
                })
              }
            />
          )}

          {/* 塊 2 — 期間 */}
          <section className="mb-6">
            <div className="flex items-center gap-4">
              <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5">
                {PERIODS.map(([p, label]) => (
                  <button
                    key={p}
                    type="button"
                    aria-pressed={p === period}
                    onClick={() => selectPeriod(p)}
                    className={`rounded px-3 py-1 text-sm ${p === period ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {configured && (
                <p className="text-sm tabular-nums text-slate-600">
                  {configured.range.fromDay} 〜 {configured.range.toDay}（JST）・
                  {jstTime(configured.asOf)} 時点
                </p>
              )}
            </div>
            {period === 'compare' && (
              <CompareControls data={configured} params={params} update={update} />
            )}
          </section>

          {/* 塊 3 — 内訳 */}
          <div data-testid="feature-usage-body" aria-busy={switching || query.isLoading}>
            {query.isLoading && (
              <div>
                <p className="text-sm text-slate-500">読み込み中…</p>
                <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-white p-3">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      data-testid="feature-usage-row-skeleton"
                      className="h-6 animate-pulse rounded bg-slate-200/60"
                    />
                  ))}
                </div>
              </div>
            )}

            {errorKind === 'permission_denied' && (
              <p className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                機能利用の記録を読む権限がありません（alys_ops_readonly に SELECT 権限が必要です）。
              </p>
            )}
            {errorKind === 'failed' && (
              <p className="text-sm text-rose-600">
                機能利用を取得できませんでした。もう一度お試しください。
                <button
                  type="button"
                  onClick={() => void query.refetch()}
                  className="ml-2 underline"
                >
                  再試行
                </button>
              </p>
            )}
            {!errorKind && data && !data.configured && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                admin-ops-api の機能利用集計が未配線です。
              </p>
            )}

            {!errorKind && configured && (
              <section className={switching ? 'opacity-50 transition-opacity' : undefined}>
                <div
                  className="mb-3 flex items-center gap-4 border-b border-slate-200"
                  role="tablist"
                >
                  {TABS.map(([t, label]) => (
                    <button
                      key={t}
                      type="button"
                      role="tab"
                      aria-selected={t === tab}
                      onClick={() => update({ tab: t, sort: null, dir: null })}
                      className={`-mb-px border-b-2 px-1 py-2 text-sm ${t === tab ? 'border-slate-900 font-medium text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {filter && (
                  <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-700">
                    workspace:{' '}
                    {filter.name ?? `削除済み workspace（${filter.workspaceId.slice(0, 8)}）`}{' '}
                    で絞り込み中
                    <button
                      type="button"
                      onClick={() => update({ workspace: null })}
                      className="text-slate-500 underline"
                    >
                      解除
                    </button>
                  </p>
                )}
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                  <UsageTables
                    data={configured}
                    tab={tab}
                    sort={sort}
                    origin={params.get('origin')}
                    onSort={toggleSort}
                    onOrigin={(o) => update({ origin: o })}
                    onSelectWorkspace={(id) => update({ workspace: id })}
                    onShowMapping={() => setMappingOpen(true)}
                  />
                </div>
              </section>
            )}
          </div>

          {!errorKind && (!data || data.configured) && (
            <Notes
              data={configured}
              mappingOpen={mappingOpen}
              onToggleMapping={() => setMappingOpen((v) => !v)}
            />
          )}
        </div>
      </div>
      <div className="block lg:hidden">
        <div className="mx-auto max-w-md px-6 py-12 text-center">
          <h1 className="text-lg font-semibold text-slate-900">PC 専用画面</h1>
          <p className="mt-3 text-sm text-slate-600">
            admin-ops は最小 1024px の画面幅でのみ動作します（情報密度が高いため）。 PC
            ブラウザでアクセスしてください。
          </p>
        </div>
      </div>
    </main>
  );
}

export { dayWithWeekday };
